import * as React from "react";
import {CircularProgress, Container, Drawer, Grid, IconButton, TextField, Tooltip, useTheme} from "@mui/material";
import {IndentSpace, PlainText, Title5} from "../../../domains/common/components/Typography";
import {
  CheckBoxWithLabel,
  FormInputAddressCtrl,
  FormInputCtrl
} from "../../../domains/common/components/FormInput";
import {errorsDescription, exceptionsTitle, realtyAreaUnitSelect, realtyTypes} from "../../../dictionaries";
import {realtyTypesMap} from "../../../dictionaries";
import {FormSelectCtrl} from "../../../domains/common/components/FormSelect";
import {useEffect, useState} from "react";
import Box from "@mui/material/Box";
import {useNotify} from "../../../domains/common/components/AlertNotify";
import {findRealtyWithCadnum, saveRealtyData} from "../realty";
import {ErrorOutline, Search} from "@mui/icons-material";
import HelpIcon from '@mui/icons-material/Help';
import {
  isCadastralNumberValid, isCostDecimalFormatValid,
  isEmpty,
  isRealtyFIASAddressValid,
  isSquareDecimalFormatValid
} from "../../../validation";
import {DarkTooltip, ErrorTooltip} from "../../../domains/common/components/Tooltip";
import {DealActionsEnums, handleDealAction} from "../../../actions";
import {dealData} from "../deal";
import {AreaUnit} from "../../../enums";
import { AreaUnitTitleMap } from "../../../dictionaries";

/**
 * @typedef RealtyValue
 * @type {Object}
 * @property {string} type
 * @property {string} cadNum
 * @property {string} realtyFiasCode
 * @property {string} regionCode
 */

export let realtyDataErrors = {
  cadNum: false,
  square: false,
  cost: false,
  realtyFIASAddress: false,
  hasErrors: () => Object.values(realtyDataErrors).includes(true)
}

/**
 *
 * @param name
 */
export const validateRealtyData = (name) => {
  if (!dealData?.realty)
    return realtyDataErrors;
  if (!name || (name && name === 'cadNum'))
    realtyDataErrors.cadNum = !isCadastralNumberValid(dealData.realty.cadNum) || isEmpty(dealData.realty.cadNum)
  if (!name || (name && name === 'square'))
    realtyDataErrors.square = !isSquareDecimalFormatValid(dealData.realty.square);
  if (!name || (name && name === 'cost'))
    realtyDataErrors.cost = !isCostDecimalFormatValid(dealData.realty.cost);
  if (!name || (name && name === 'realtyFIASAddress'))
    realtyDataErrors.realtyFIASAddress = !isRealtyFIASAddressValid(dealData.realty.address);

  return realtyDataErrors;
}

/**
 *
 * @param RealtyValues
 * @returns {Element}
 * @constructor
 */
export default function RealtyFormContainer({values}) {
  const [drawerState, setDrawerState] = useState(false);
  const [realty, setRealty] = useState(values)
  const [loading, setLoading] = useState(false)
  const [addr, setAddr] = useState({
    fiasCode: values?.fiasCode,
    fullAddress: values?.fullAddress,
    regionCode: values?.regionCode,
    manualFullAddress: values?.manualFullAddress
  })
  const [hasChange, setHasChange] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState()

  const theme = useTheme();
  const {openNotify} = useNotify();

  useEffect(() => {
    validateData();

    setHasError(realtyDataErrors.hasErrors())
  }, [])

  const validateData = (name) => {
    validateRealtyData(name)
    setError(realtyDataErrors)
  }

  const onChange = (event) => {
    setHasChange(true);

    if(!dealData.realty.areaUnit) {
      dealData.realty.areaUnit = AreaUnit.SQUARE_METER
    }

    dealData.realty[event.target.name] = event.target.value
    setRealty({...dealData.realty});

    validateData(event.target.name)
    setError(realtyDataErrors)
    setHasError(realtyDataErrors.hasErrors())
  }

  const findObjectCadNum = async (cadNum) => {
    if (!cadNum)
      return;

    setLoading(true);

    const result = await findRealtyWithCadnum(cadNum)

    if (result.ok) {
      if (result.data) {
        setRealty(result.data);
        dealData.realty = result.data;
        if(!dealData.realty.areaUnit) {
          dealData.realty.areaUnit = AreaUnit.SQUARE_METER
        }
        validateRealtyData()
        openNotify({
          msg: "Нашёл",
          duration: 1000,
          type: 'success'
        })
      } else {
        openNotify({
          msg: "Объект не найден",
          duration: 1500,
          type: 'error'
        })
      }
    } else {
      openNotify({
        msg: exceptionsTitle[result.message] || result.message || "Ошибка поиска объекта",
        duration: 2000,
        type: 'error'
      })
    }

    setLoading(false)
  }

  const onChangeSpecification = (event) => {
    setHasChange(true);
    if (!dealData?.realty?.specification) {
      dealData.realty.specification = {};
    }
    dealData.realty.specification[event.target.name] = event.target.checked
    setRealty({...dealData.realty});
  }

  const setRealtyAddressData = (option) => {
    if (!option) {
      dealData.realty.fiasCode = ''
      dealData.realty.fullAddress = ''
      dealData.realty.regionCode = ''
      dealData.realty.address = {}

      return;
    }

    dealData.realty.address = option?.address;

    if (option?.address?.FiasID) {
      dealData.realty.fiasCode = option.address.FiasID
    }

    if (option?.address?.UnrestrictedValue) {
      dealData.realty.fullAddress = option.address.UnrestrictedValue
    }

    if (option?.regionCode) {
      dealData.realty.regionCode = option.regionCode
    }
  }

  const onSelectAddress = (option) => {
    setHasChange(true);

    if (!option) {
      setRealtyAddressData(null)
      setAddr({fiasCode: '', fullAddress: '', regionCode: '', region: '', regionType: '', data: null})
    } else {
      setRealtyAddressData(option)
      setAddr(Object.assign(addr, {fiasCode: option?.id, fullAddress: option?.label, regionCode: option?.regionCode}))
    }

    validateData('realtyFIASAddress')

    setRealty({...dealData.realty});
  }

  const onDrawerClose = async () => {
    realtyDataErrors.hasErrors() ? setHasError(true) : setHasError(false);

    if (!hasChange) {
      setDrawerState(false);
      setRealty(dealData.realty);
      return;
    }

    const result = await saveRealtyData();

    if (result.ok) {
      handleDealAction(DealActionsEnums.CHANGE_REALTY)
      openNotify({msg: "Данные сохранены успешно", duration: 1000, type: 'success'})
    } else {
      openNotify({
        msg: exceptionsTitle[result.message] || result.message || "Ошибка сохранения данных",
        duration: 3000,
        type: 'error',
        errors: result.errors,
      })
    }

    setDrawerState(false);
    setRealty(dealData.realty);
  }
    
  return (
    <>
      <Drawer open={drawerState}
              anchor='right'
              onClose={onDrawerClose}
      >
        <Container sx={{width: 600, padding: 1}}>
          <input className='hidden' name='fullAddress' value={addr.fullAddress}/>
          <input className='hidden' name='fiasCode' value={addr.fiasCode}/>
          <input className='hidden' name='regionCode' value={addr.regionCode}/>
          <Grid container spacing={1} xs={12}>
            <Grid item xs={1}><Title5 title="Объект"/></Grid>
            <Grid item xs={12}><FormSelectCtrl data={realtyTypes} label="Тип объекта недвижимости" required
                                               name="type" value={realty?.type}
                                               onChange={onChange} emptyOpt={true}/></Grid>
            {realty?.type === 'land' ? <>
              <Grid item xs={9}>
                <CheckBoxWithLabel
                  label="Назначение земель ЛПХ, ИЖС, Садоводство"
                  name="landUsageType"
                  checked={realty?.specification?.landUsageType}
                  onChange={onChangeSpecification}/>
              </Grid>
              <Grid item xs={3}>
                <DarkTooltip placement="top" sx={{fontSize: '16px'}}
                             title="Ведения личного подсобного хозяйства, огородничества, садоводства, индивидуального гаражного или индивидуального жилищного строительства; земельных участков из земель сельскохозяйственного назначения. Стоимость госпошлины 700 руб.">
                  <HelpIcon sx={{color: '#a5a5a5', fontSize: '18px', pt: '12px'}}/>
                </DarkTooltip>
              </Grid>
            </> : void 0}
            <Grid item xs={12}>
              <FormInputCtrl label="Кадастровый номер" value={realty?.cadNum} required
                             name="cadNum" onChange={onChange} slotProps={{pattern: '/\d/gmi'}} error={error?.cadNum}/>
              {
                error?.cadNum ?
                  <>
                    <IndentSpace size={5}/>
                    <ErrorTooltip placement="bottom" title={errorsDescription.CAD_NUM_ERROR}/>
                  </> : void 0
              }
              {!realty.id ?
                <Tooltip placement="right" title="Поиск объекта по сделкам">
                  <IconButton type="button" sx={{p: '8px'}} aria-label="search"
                              onClick={() => findObjectCadNum(realty?.cadNum)}>
                    {loading ? <CircularProgress size="10px"/> : <Search sx={{color: '#adadad'}}/>}
                  </IconButton>
                </Tooltip> : void 0}
            </Grid>
            <Grid item xs={12} sx={{display: 'flex'}}>
              <FormInputAddressCtrl onSelect={onSelectAddress} defaultValue={realty.fullAddress} width="450px"
                                    error={error?.realtyFIASAddress} label="Поиск адреса объекта ФИАС"/>
              {
                error?.realtyFIASAddress ?
                  <>
                    <IndentSpace size={5}/>
                    <ErrorTooltip placement="bottom" title={errorsDescription.REALTY_FIAS_ADDR_ERROR}/>
                  </>
                  : void 0
              }
            </Grid>
            <Grid item xs={12}><Box sx={{fontWeight: 'light', fontSize: 'small'}}>{realty.fullAddress}</Box></Grid>
            <Grid item xs={12}>
              <TextField
                defaultValue={realty?.manualFullAddress}
                variant="outlined"
                focused={!!realty?.manualFullAddress}
                label="Полный адрес по документам"
                onChange={onChange}
                multiline
                size="sm"
                rows={4}
                sx={{
                  width: '500px',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: theme.palette.border.secondary,
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.border.secondary,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.border.secondary,
                      borderWidth: '1px',
                    },
                  },

                }}
                name="manualFullAddress"

              />
            </Grid>
            <Grid item xs={12} style={{height: 10}}></Grid>
            <Grid item xs={12}>
              <FormInputCtrl required label="Площадь объекта" value={realty?.square}
                             name="square" onChange={onChange} error={error?.square}/>
              {
                error?.square ?
                  <>
                    <IndentSpace size={5}/>
                    <ErrorTooltip placement="bottom" title={errorsDescription.REALTY_SQUARE_ERROR}/>
                  </>
                  : void 0
              }
              <IndentSpace size={5} />
              <FormSelectCtrl data={realtyAreaUnitSelect} label="Единица измерения" required
                              name="areaUnit" value={realty?.areaUnit || AreaUnit.SQUARE_METER}
                              onChange={onChange} emptyOpt={true} width={150}/>
            </Grid>
            <Grid item xs={12} style={{height: 10}}></Grid>
            <Grid item xs={12}>
              <FormInputCtrl label="Кадастровая стоимость, руб." value={realty?.cost}
                             name="cost" onChange={onChange} error={error?.cost}/>
              {
                error?.cost ?
                  <>
                    <IndentSpace size={5}/>
                    <ErrorTooltip placement="bottom" title={errorsDescription.REALTY_COST_ERROR}/>
                  </>
                  : void 0
              }
            </Grid>
          </Grid>
        </Container>
      </Drawer>
      <Grid container xs={12} onClick={() => setDrawerState(true)} sx={{cursor: 'pointer'}}>
        {hasError ?
          <Grid item sx={{pt: '8px', pr: '10px'}} alignItems="center"
                justifyContent="center">
            <ErrorOutline sx={{color: '#e82d2d', fontSize: '24px'}}/>
          </Grid> : ''}
        <Grid item sx={{pt: '8px', pr: '15px'}} alignItems="center"
              justifyContent="center">
          <Title5 title="Объект"/>
        </Grid>
        <Grid item size="auto" sx={{pr: 2}}>
          <Box sx={{fontWeight: 'medium'}}>{realty?.type ? 'Тип' : ''}</Box>
          <PlainText>{realtyTypesMap[realty?.type]?.slice(0, 15)} {realtyTypesMap[realty?.type]?.length > 15 ? '...' : void 0}</PlainText>
        </Grid>
        <Grid item size="auto" sx={{pr: 2}}>
          <Box sx={{fontWeight: 'medium'}}>{realty?.cadNum ? 'Кадастровый номер' : ''}</Box>
          <PlainText>{realty?.cadNum}</PlainText>
        </Grid>
        <Grid item size="auto" sx={{pr: 2}}>
          <Box sx={{fontWeight: 'medium'}}>{realty?.fullAddress ? 'Адрес' : ''}</Box>
          <PlainText>{realty?.fullAddress.slice(0, 80)} {realty?.fullAddress?.length > 80 ? '...' : void 0}</PlainText>
        </Grid>
        <Grid item size="auto">
          <Box sx={{fontWeight: 'medium'}}>{realty?.square ? 'Площадь объекта' : ''}</Box>
          <PlainText>{realty?.square} {realty.square && AreaUnitTitleMap[realty?.areaUnit]}</PlainText>
        </Grid>
      </Grid>
    </>
  )
}